import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { cx } from '../../../internal/cx.js';
function clampPct(value, max) {
    if (!(max > 0))
        return 0;
    return Math.min(100, Math.max(0, (value / max) * 100));
}
function round(n) {
    return Math.round(n * 100) / 100;
}
const SEGMENT_DEFAULTS = ['data-1', 'data-2', 'data-3', 'data-4', 'data-5'];
/**
 * Linear progress bar; `value` undefined renders an indeterminate animation.
 * Also a quota meter (`semantics="meter"`) and a stacked bar (`segments`).
 */
export const Progress = forwardRef(function Progress({ value, max = 100, label, size = 'md', tone, semantics = 'progressbar', thresholds, segments, valueText, getValueLabel, className, ...rest }, ref) {
    const meter = semantics === 'meter';
    if (!meter && !segments) {
        // The original 0.3 progress bar, unchanged (Radix Progress).
        const pct = value === undefined || value === null
            ? undefined
            : Math.min(100, Math.max(0, (value / max) * 100));
        return (_jsx(ProgressPrimitive.Root, { ref: ref, value: value ?? null, max: max, "aria-label": label, ...(valueText !== undefined
                ? { getValueLabel: () => valueText }
                : getValueLabel && { getValueLabel }), ...rest, "data-size": size, "data-tone": tone ?? 'accent', "data-indeterminate": pct === undefined || undefined, className: cx('mn-progress', className), children: _jsx(ProgressPrimitive.Indicator, { className: "mn-progress__bar", style: pct === undefined ? undefined : { width: `${pct}%` } }) }));
    }
    // Meter and segmented bars: hand-built so the role and value text are exact.
    const parts = segments?.map((s, i) => ({
        ...s,
        tone: s.tone ?? SEGMENT_DEFAULTS[i % SEGMENT_DEFAULTS.length] ?? 'data-1',
    }));
    const amount = parts ? parts.reduce((sum, s) => sum + Math.max(0, s.value), 0) : (value ?? 0);
    const over = amount > max;
    let resolvedTone = tone ?? (meter ? 'neutral' : 'accent');
    if (meter && tone === undefined) {
        if (over || (thresholds?.danger !== undefined && amount >= thresholds.danger)) {
            resolvedTone = 'danger';
        }
        else if (thresholds?.warning !== undefined && amount >= thresholds.warning) {
            resolvedTone = 'warning';
        }
    }
    const generatedText = parts
        ? `${parts.map((s) => `${s.label} ${round(s.value)}`).join(', ')} (${round(amount)} of ${round(max)})`
        : `${round(amount)} of ${round(max)}${over ? ', over the limit' : ''}`;
    const role = meter ? 'meter' : 'progressbar';
    let offset = 0;
    return (_jsx("div", { ref: ref, role: role, "aria-label": label, "aria-valuemin": 0, "aria-valuemax": max, "aria-valuenow": Math.min(amount, max), "aria-valuetext": valueText ?? generatedText, ...rest, "data-size": size, "data-tone": resolvedTone, "data-semantics": semantics, "data-segmented": parts ? '' : undefined, "data-over": over || undefined, "data-value": amount, "data-max": max, className: cx('mn-progress', className), children: parts ? (parts.map((s, i) => {
            const width = Math.max(0, Math.min(clampPct(s.value, max), 100 - offset));
            const start = offset;
            offset += width;
            return (_jsx("div", { className: "mn-progress__segment", "data-tone": s.tone, style: { insetInlineStart: `${start}%`, width: `${width}%` } }, i));
        })) : (_jsx("div", { className: "mn-progress__bar", style: { width: `${clampPct(amount, max)}%` } })) }));
});
//# sourceMappingURL=progress-bar.js.map